import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import Header from './components/Header';
import DashboardWelcomeOverlay from './components/DashboardWelcomeOverlay';
import { useAuth } from './context/AuthContext';
import { logUserEvent } from './utils/logger';

import {
    Container,
    Typography,
    Card,
    CardContent,
    Box,
    CircularProgress,
    Alert,
    Tooltip,
    Button,
    Stack,
    Chip,
    Snackbar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import MicIcon from '@mui/icons-material/Mic';
import QuizIcon from '@mui/icons-material/Quiz';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DownloadIcon from '@mui/icons-material/Download';
import LockIcon from '@mui/icons-material/Lock';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

const Dashboard = () => {
    const PASS_THRESHOLD = 85; // percent required to mark as passed
    // Be defensive: tests may render without AuthProvider
    const auth = useAuth();
    const user = auth?.user;
    const authLoading = auth?.loading;
    const location = useLocation();
    const navigate = useNavigate();
    const [courses, setCourses] = useState({});
    const [passedCourseResults, setPassedCourseResults] = useState(new Map());
    const [mediaAvailability, setMediaAvailability] = useState(new Map());
    const [courseProgress, setCourseProgress] = useState(new Map());
    const [expandedDescriptions, setExpandedDescriptions] = useState(new Set());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showProfileSuccess, setShowProfileSuccess] = useState(false);
    const [highlightCourseId, setHighlightCourseId] = useState(null);
    const [certSnackbar, setCertSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [failedAttempts, setFailedAttempts] = useState(new Map());
    const [blockedPopupOpen, setBlockedPopupOpen] = useState(false);
    const [blockedCourseTitle, setBlockedCourseTitle] = useState('');

    // Check if redirected from profile update
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('profileUpdated') === 'true') {
            setShowProfileSuccess(true);
            const courseId = params.get('courseId');
            if (courseId) {
                setHighlightCourseId(courseId);
                // Scroll to the course after a brief delay
                setTimeout(() => {
                    const element = document.getElementById(`course-${courseId}`);
                    if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                }, 500);
            }
            // Clean up URL
            navigate(location.pathname, { replace: true });
        }
    }, [location, navigate]);

    const handleRegenerateCertificate = async (courseId) => {
        const result = passedCourseResults.get(String(courseId));
        const course = Object.values(courses).flat().find(c => String(c.id) === String(courseId));
        const userName = user?.name;
        const userSurname = user?.surname;

        if (!result || !course || !userName) {
            alert('Could not find the necessary data to regenerate the certificate.');
            return;
        }

        try {
            const { default: generateCertificate } = await import('./utils/certificateGenerator');
            const genResult = await generateCertificate(
                course.title,
                (course.complexity || ''),
                userName,
                userSurname,
                course.title, // Use course title for filename
                result.authenticity_code
            );

            if (genResult.success) {
                setCertSnackbar({
                    open: true,
                    message: `🎉 Certificate downloaded successfully! Check your downloads folder for ${genResult.fileName}`,
                    severity: 'success'
                });
                logUserEvent('certificate_download', `Downloaded certificate for course: ${course.title}`);
            } else {
                setCertSnackbar({
                    open: true,
                    message: `❌ Error generating certificate: ${genResult.error}`,
                    severity: 'error'
                });
            }
        } catch (error) {
            console.error('Failed to regenerate certificate:', error);
            setCertSnackbar({
                open: true,
                message: '❌ Failed to generate certificate. Please try again.',
                severity: 'error'
            });
        }
    };

    useEffect(() => {
        const fetchAllData = async () => {
            try {
                // First, fetch all available courses
                const coursesResponse = await axios.get('/api/courses/dashboard/');
                const coursesByCategory = coursesResponse.data.reduce((acc, course) => {
                    const category = course.category || 'Uncategorized';
                    if (!acc[category]) {
                        acc[category] = [];
                    }
                    acc[category].push(course);
                    return acc;
                }, {});

                // Sort courses within each category by complexity (Basic, Intermediate, Specialist)
                const complexityOrder = { 'Basic': 1, 'Intermediate': 2, 'Specialist': 3, 'Beginner': 0 };
                Object.keys(coursesByCategory).forEach(category => {
                    coursesByCategory[category].sort((a, b) => {
                        const orderA = complexityOrder[a.complexity] || 999;
                        const orderB = complexityOrder[b.complexity] || 999;
                        return orderA - orderB;
                    });
                });

                setCourses(coursesByCategory);

                // Fetch media and progress for each course (non-blocking)
                const allCourses = Object.values(coursesByCategory).flat();

                (async () => {
                    const mediaMap = new Map();
                    const progressMap = new Map();

                    await Promise.allSettled(
                        allCourses.map(async (course) => {
                            // Fetch Media
                            try {
                                const mediaResponse = await axios.get(
                                    `/api/student/courses/${course.id}/media_availability`,
                                    { timeout: 4000 }
                                );
                                mediaMap.set(course.id, mediaResponse.data);
                            } catch (error) {
                                mediaMap.set(course.id, { has_video: false, has_podcast: false });
                            }

                            // Fetch Progress
                            try {
                                const progressResponse = await axios.get(`/api/student/courses/${course.id}/progress`);
                                progressMap.set(course.id, progressResponse.data);
                            } catch (error) {
                                progressMap.set(course.id, { textbook_completed: false });
                            }
                        })
                    );
                    setMediaAvailability(mediaMap);
                    setCourseProgress(progressMap);
                })();

                // Then, fetch the user's course results (AuthContext)
                const userId = user?.id;
                if (userId) {
                    const resultsResponse = await axios.get(`/api/users/${userId}/results`, { timeout: 5000 });
                    const passedMap = new Map();
                    resultsResponse.data.forEach(result => {
                        if ((result.score || 0) >= PASS_THRESHOLD) {
                            // Convert course_id to string to match course.id format
                            passedMap.set(String(result.course_id), result);
                        }
                    });
                    setPassedCourseResults(passedMap);

                    // Fetch failed attempts for today
                    try {
                        const attemptsResponse = await axios.get(`/api/courses/attempts/today?user_id=${userId}`);
                        const attemptsMap = new Map(Object.entries(attemptsResponse.data).map(([k, v]) => [k, v]));
                        setFailedAttempts(attemptsMap);
                    } catch (err) {
                        console.error('Failed to fetch attempts:', err);
                    }
                }
            } catch (error) {
                console.error('Error fetching data:', error);
                setError('Failed to load dashboard data. Please make sure the backend is running.');
            } finally {
                setLoading(false);
            }
        };

        // Wait for auth to finish initializing
        if (!authLoading) {
            if (!user) {
                setError('Please sign in to view your dashboard.');
                setLoading(false);
            } else {
                fetchAllData();
                logUserEvent('dashboard_access', `Accessed dashboard with ${Object.values(courses).flat().length} available courses`);
            }
        }
    }, [user, authLoading, location]);

    if (loading || authLoading) {
        return (
            <Box sx={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #0054A6 0%, #00386e 55%, #001f3f 100%)'
            }}>
                <Container sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CircularProgress />
                </Container>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #0054A6 0%, #00386e 55%, #001f3f 100%)'
            }}>
                <Container>
                    <Alert severity="error" sx={{ mt: 4 }}>{error}</Alert>
                </Container>
            </Box>
        );
    }

    return (
        <Box sx={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #0054A6 0%, #00386e 55%, #001f3f 100%)'
        }}>
            <Header />
            <DashboardWelcomeOverlay />

            {/* Profile update success banner */}
            <Snackbar
                open={showProfileSuccess}
                autoHideDuration={6000}
                onClose={() => setShowProfileSuccess(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={() => setShowProfileSuccess(false)} severity="success" sx={{ width: '100%' }}>
                    ✅ Profile updated! You can now download your certificate below.
                </Alert>
            </Snackbar>

            <Container maxWidth="xl" sx={{ mt: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom sx={{
                    textAlign: 'center',
                    mb: 4,
                    color: 'white',
                    textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
                }}>
                    Course Dashboard
                </Typography>
                {Object.keys(courses).length > 0 ? (
                    Object.entries(courses).map(([category, courseList]) => (
                        <Box key={category} sx={{ mb: 4 }}>
                            <Typography variant="h5" component="h2" gutterBottom sx={{
                                borderBottom: 1,
                                borderColor: 'rgba(255,255,255,0.3)',
                                pb: 1,
                                color: 'white',
                                textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
                            }}>
                                {category}
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mt: 2 }}>
                                {courseList.map(course => {
                                    const isPassed = passedCourseResults.has(String(course.id));
                                    const progress = courseProgress.get(course.id);
                                    const textbookCompleted = progress?.textbook_completed || false;

                                    const complexityText = (course.complexity || '').toUpperCase();

                                    // Medal-themed colors: bronze, silver, gold
                                    const complexityStyle =
                                        complexityText === 'BASIC' || complexityText === 'BEGINNER' ?
                                            { bgcolor: '#CD7F32', color: 'white' } : // Bronze
                                            complexityText === 'INTERMEDIATE' ?
                                                { bgcolor: '#C0C0C0', color: 'black' } : // Silver
                                                { bgcolor: '#FFD700', color: 'black' }; // Gold

                                    const isHighlighted = highlightCourseId === course.id;

                                    return (
                                        <Card
                                            key={course.id}
                                            id={`course-${course.id}`}
                                            sx={{
                                                width: '90%',
                                                maxWidth: '900px',
                                                borderRadius: '12px',
                                                boxShadow: isHighlighted
                                                    ? '0 10px 25px -5px rgba(0, 0, 0, 0.2), 0 8px 10px -6px rgba(0, 0, 0, 0.1)'
                                                    : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                                                border: isHighlighted ? '3px solid #4caf50' : 'none',
                                                overflow: 'hidden',
                                                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                                '&:hover': {
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
                                                },
                                                animation: isHighlighted ? 'pulse 2s ease-in-out 3' : 'none',
                                                '@keyframes pulse': {
                                                    '0%, 100%': { transform: 'scale(1)' },
                                                    '50%': { transform: 'scale(1.02)' }
                                                }
                                            }}>
                                            {/* Card Header Section */}
                                            <Box sx={{
                                                px: { xs: 2, sm: 4 },
                                                pt: 3,
                                                pb: 2,
                                                borderBottom: '1px solid #f3f4f6',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'flex-start',
                                                flexWrap: 'wrap',
                                                gap: 1
                                            }}>
                                                <Box sx={{ flex: 1, minWidth: 200 }}>
                                                    <Typography
                                                        variant="h6"
                                                        component="div"
                                                        sx={{
                                                            fontWeight: 600,
                                                            fontSize: '1.25rem',
                                                            color: '#1f2937',
                                                            mb: 1
                                                        }}
                                                    >
                                                        {course.title}
                                                    </Typography>
                                                    {isPassed && (
                                                        <Box sx={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: 0.75,
                                                            bgcolor: '#d1fae5',
                                                            color: '#059669',
                                                            px: 1.5,
                                                            py: 0.5,
                                                            borderRadius: '20px',
                                                            fontSize: '0.875rem',
                                                            fontWeight: 600
                                                        }}>
                                                            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                                                            </svg>
                                                            Course Passed
                                                        </Box>
                                                    )}
                                                </Box>
                                                {complexityText && (
                                                    <Chip
                                                        label={complexityText}
                                                        size="small"
                                                        sx={{
                                                            fontWeight: 700,
                                                            fontSize: '0.7rem',
                                                            letterSpacing: '0.05em',
                                                            minWidth: '100px',
                                                            borderRadius: '6px',
                                                            ...complexityStyle
                                                        }}
                                                    />
                                                )}
                                            </Box>

                                            {/* Card Body Section */}
                                            <Box sx={{ px: { xs: 2, sm: 4 }, py: 3 }}>
                                                <Box
                                                    sx={{
                                                        maxHeight: expandedDescriptions.has(course.id) ? 'none' : '120px',
                                                        overflow: 'hidden',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: expandedDescriptions.has(course.id) ? 'unset' : 4,
                                                        WebkitBoxOrient: 'vertical',
                                                        '& h1, & h2, & h3': { fontSize: '1rem', fontWeight: 'bold', mt: 1, mb: 0.5 },
                                                        '& p': { fontSize: '0.875rem', my: 0.5, lineHeight: 1.6 },
                                                        '& ul, & ol': { fontSize: '0.875rem', my: 0.5, pl: 2 },
                                                        color: '#6b7280',
                                                        transition: 'all 0.3s ease'
                                                    }}
                                                >
                                                    <ReactMarkdown>
                                                        {(() => {
                                                            // Remove first line if it's the redundant "Title — Complexity" format
                                                            const lines = (course.description || '').split('\n');
                                                            const firstLine = lines[0] || '';
                                                            // Check if first line matches pattern: "Title — Complexity"
                                                            if (firstLine.includes('—') &&
                                                                (firstLine.toLowerCase().includes('basic') ||
                                                                    firstLine.toLowerCase().includes('intermediate') ||
                                                                    firstLine.toLowerCase().includes('specialist') ||
                                                                    firstLine.toLowerCase().includes('beginner'))) {
                                                                return lines.slice(1).join('\n');
                                                            }
                                                            return course.description;
                                                        })()}
                                                    </ReactMarkdown>
                                                </Box>
                                                {course.description && course.description.length > 200 && (
                                                    <Button
                                                        size="small"
                                                        onClick={() => {
                                                            setExpandedDescriptions(prev => {
                                                                const newSet = new Set(prev);
                                                                if (newSet.has(course.id)) {
                                                                    newSet.delete(course.id);
                                                                } else {
                                                                    newSet.add(course.id);
                                                                    // Log when user expands course details
                                                                    logUserEvent('course_details_access', `Viewed details for course: ${course.title}`);
                                                                }
                                                                return newSet;
                                                            });
                                                        }}
                                                        endIcon={expandedDescriptions.has(course.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                        sx={{
                                                            mt: 1,
                                                            textTransform: 'none',
                                                            color: '#6366f1',
                                                            fontWeight: 600,
                                                            fontSize: '0.9rem',
                                                            p: 0,
                                                            '&:hover': {
                                                                backgroundColor: 'transparent',
                                                                textDecoration: 'underline'
                                                            }
                                                        }}
                                                    >
                                                        {expandedDescriptions.has(course.id) ? 'Show less' : 'Show details'}
                                                    </Button>
                                                )}
                                            </Box>

                                            {/* Card Footer Section */}
                                            <Box sx={{
                                                px: { xs: 2, sm: 4 },
                                                py: 2.5,
                                                bgcolor: '#f9fafb',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                gap: 2
                                            }}>
                                                {/* Resources Group */}
                                                <Stack direction="row" spacing={1.5}>
                                                    <Button
                                                        component={RouterLink}
                                                        to={`/textbook/${course.id}`}
                                                        variant="outlined"
                                                        startIcon={<MenuBookIcon />}
                                                        endIcon={textbookCompleted ? <CheckCircleOutlineIcon color="success" fontSize="small" /> : null}
                                                        sx={{
                                                            borderColor: textbookCompleted ? '#4caf50' : '#e5e7eb',
                                                            color: '#6b7280',
                                                            backgroundColor: 'white',
                                                            textTransform: 'none',
                                                            fontWeight: 600,
                                                            borderRadius: '8px',
                                                            px: 2.5,
                                                            py: 1,
                                                            '&:hover': {
                                                                borderColor: '#6366f1',
                                                                color: '#6366f1',
                                                                backgroundColor: '#eef2ff'
                                                            }
                                                        }}
                                                    >
                                                        Textbook
                                                    </Button>

                                                    {(() => {
                                                        const media = mediaAvailability.get(course.id);
                                                        const hasVideo = media?.has_video || false;
                                                        const hasPodcast = media?.has_podcast || false;

                                                        return (
                                                            <>
                                                                {hasPodcast && (
                                                                    <Button
                                                                        component={RouterLink}
                                                                        to={`/podcast/${course.id}`}
                                                                        variant="outlined"
                                                                        startIcon={<MicIcon />}
                                                                        sx={{
                                                                            borderColor: '#e5e7eb',
                                                                            color: '#6b7280',
                                                                            backgroundColor: 'white',
                                                                            textTransform: 'none',
                                                                            fontWeight: 600,
                                                                            borderRadius: '8px',
                                                                            px: 2.5,
                                                                            py: 1,
                                                                            '&:hover': {
                                                                                borderColor: '#ed6c02',
                                                                                color: '#ed6c02',
                                                                                backgroundColor: '#fff7ed'
                                                                            }
                                                                        }}
                                                                    >
                                                                        Podcast
                                                                    </Button>
                                                                )}
                                                            </>
                                                        );
                                                    })()}
                                                </Stack>

                                                {/* Primary Action */}
                                                {(() => {
                                                    const attempts = failedAttempts.get(course.id) || 0;
                                                    const isBlocked = attempts >= 3;

                                                    if (isPassed) {
                                                        return (
                                                            <Button
                                                                variant="contained"
                                                                size="small"
                                                                startIcon={<DownloadIcon sx={{ fontSize: '1rem' }} />}
                                                                onClick={() => handleRegenerateCertificate(course.id)}
                                                                sx={{
                                                                    bgcolor: '#10b981',
                                                                    color: 'white',
                                                                    textTransform: 'none',
                                                                    fontWeight: 600,
                                                                    fontSize: '0.85rem',
                                                                    borderRadius: '8px',
                                                                    px: 2,
                                                                    py: 0.75,
                                                                    boxShadow: '0 4px 6px -1px rgba(16, 185, 129, 0.3)',
                                                                    '&:hover': {
                                                                        bgcolor: '#059669',
                                                                        transform: 'translateY(-1px)'
                                                                    },
                                                                    animation: isHighlighted ? 'glow 1.5s ease-in-out infinite' : 'none',
                                                                    '@keyframes glow': {
                                                                        '0%, 100%': { boxShadow: '0 0 5px rgba(16, 185, 129, 0.5)' },
                                                                        '50%': { boxShadow: '0 0 20px rgba(16, 185, 129, 0.8)' }
                                                                    }
                                                                }}
                                                            >
                                                                Download Certificate
                                                            </Button>
                                                        );
                                                    }
                                                    const isLocked = !textbookCompleted;

                                                    if (isLocked) {
                                                        return (
                                                            <Tooltip title="🔒 Locked: Please complete the textbook first.">
                                                                <span>
                                                                    <Button
                                                                        variant="contained"
                                                                        startIcon={<LockIcon />}
                                                                        disabled
                                                                        sx={{
                                                                            bgcolor: '#e5e7eb',
                                                                            color: '#9ca3af',
                                                                            textTransform: 'none',
                                                                            fontWeight: 600,
                                                                            borderRadius: '8px',
                                                                            px: 2.5,
                                                                            py: 1,
                                                                        }}
                                                                    >
                                                                        Certification Exam
                                                                    </Button>
                                                                </span>
                                                            </Tooltip>
                                                        );
                                                    }

                                                    return (
                                                        <Button
                                                            component={isBlocked ? 'button' : RouterLink}
                                                            to={isBlocked ? undefined : `/quiz/${course.id}`}
                                                            variant="contained"
                                                            startIcon={<QuizIcon />}
                                                            onClick={isBlocked ? () => {
                                                                setBlockedCourseTitle(course.title);
                                                                setBlockedPopupOpen(true);
                                                            } : undefined}
                                                            sx={{
                                                                bgcolor: isBlocked ? '#9ca3af' : '#3b82f6',
                                                                color: 'white',
                                                                textTransform: 'none',
                                                                fontWeight: 600,
                                                                borderRadius: '8px',
                                                                px: 2.5,
                                                                py: 1,
                                                                boxShadow: isBlocked ? 'none' : '0 4px 6px -1px rgba(59, 130, 246, 0.3)',
                                                                '&:hover': {
                                                                    bgcolor: isBlocked ? '#6b7280' : '#2563eb',
                                                                    transform: isBlocked ? 'none' : 'translateY(-1px)'
                                                                }
                                                            }}
                                                        >
                                                            Certification Exam
                                                        </Button>
                                                    );
                                                })()}
                                            </Box>
                                        </Card>
                                    );
                                })}
                            </Box>
                        </Box>
                    ))
                ) : (
                    <Alert severity="info" sx={{ mt: 4 }}>No courses found.</Alert>
                )}
            </Container>

            {/* Certificate download snackbar */}
            <Snackbar
                open={certSnackbar.open}
                autoHideDuration={6000}
                onClose={() => setCertSnackbar({ ...certSnackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setCertSnackbar({ ...certSnackbar, open: false })}
                    severity={certSnackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {certSnackbar.message}
                </Alert>
            </Snackbar>

            {/* Blocked Attempt Dialog */}
            <Dialog open={blockedPopupOpen} onClose={() => setBlockedPopupOpen(false)}>
                <DialogTitle>⚠️ Daily Limit Reached</DialogTitle>
                <DialogContent>
                    <Typography>
                        You've reached the daily limit of 3 failed attempts for <strong>{blockedCourseTitle}</strong>.
                    </Typography>
                    <Typography sx={{ mt: 2 }}>
                        Please study the textbook, podcast and video again.
                        You may retry this test again tomorrow, it will again start with a 'clean slate'.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBlockedPopupOpen(false)} variant="contained">
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Dashboard;
